import {
  Modal,
  IconButton,
  ButtonSize,
  Button,
  ButtonVariant,
  IconButtonVariant,
  IconButtonSize,
  Show
} from "shared/components";
import { XClose } from "untitledui-js-base";
import { AsyncSelect } from "../../components/AsyncSelect/AsyncSelect";
import {
  loadPropertyOptions,
  loadUnitOptions
} from "../../../api/landlord/asyncUnitProperties";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import addressApi from "../../../api/landlord/address";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../../utils/error";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { RentAmountData, SetRentAmountOptions } from "./SetRentAmountOptions";
import { Application } from "types/application";
import { cx } from "shared/utils";
import { useAuthContext } from "providers/Auth";

enum View {
  ChooseUnit = "ChooseUnit",
  SetRentOptions = "SetRentOptions"
}

export const MoveModal = ({
  onClose,
  applicationId
}: {
  onClose: () => void;
  applicationId: string;
}) => {
  const { pms } = useAuthContext();
  const { Unit, unit, Property } = useUnitsPropertiesDictionary();
  const queryClient = useQueryClient();
  const [unitError, setUnitError] = useState("");
  const [propertyError, setPropertyError] = useState("");
  const [selectedPropertyId, setSelectedPropertyId] = useState<
    number | undefined
  >();
  const [selectedUnitId, setSelectedUnitId] = useState<number | undefined>();
  const [selectedUnitName, setSelectedUnitName] = useState<
    string | undefined
  >();
  const [manualRentsInput, setManualRentsInput] = useState<RentAmountData>();
  const [rentChangeType, setRentChangeType] = useState<
    "use_current" | "use_listed" | "manual"
  >("use_current");

  const [view, setView] = useState<View>(View.ChooseUnit);

  const { data: application } = useQuery<Application>({
    queryKey: ["APPLICATION", applicationId],
    queryFn: () => addressApi.getApplicationById(applicationId || ""),
    enabled: Boolean(applicationId)
  });

  const handleClose = () => {
    setSelectedPropertyId(undefined);
    setSelectedUnitId(undefined);
    setManualRentsInput(undefined);
    setView(View.ChooseUnit);
    onClose();
    setRentChangeType("use_current");
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (
      rentData: {
        change_type: "use_current" | "use_listed" | "manual";
      } & RentAmountData
    ) =>
      addressApi.updateApplicationUnit(
        applicationId,
        Number(selectedUnitId),
        rentData
      ),
    onSuccess: () => {
      toast.success("Application successfully updated");
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", applicationId]
      });
      queryClient.invalidateQueries({
        queryKey: ["APPLICANTS_TABLE"]
      });
      handleClose();
    },
    onError: (e) => {
      toast.error(getErrorMessage(e));
    }
  });

  const handleContinue = () => {
    if (view === View.ChooseUnit) setView(View.SetRentOptions);
  };

  return (
    <Modal
      opened={Boolean(applicationId)}
      onClose={handleClose}
      panelClassName={cx(
        "max-w-[600px] w-full",
        view === View.SetRentOptions &&
          (pms.isManageAmerica ? "max-w-[877px]" : "max-w-[697px]")
      )}
    >
      <div className="p-6 border-b border-alpha-100 relative">
        <p className="m-0 text-xl font-bold leading-8">
          <Show if={view === View.ChooseUnit}>
            Move application to another {unit}
          </Show>
          <Show if={view === View.SetRentOptions}>
            Set rent amount for application
          </Show>
        </p>
        <IconButton
          variant={IconButtonVariant.BASE}
          size={IconButtonSize.LG}
          borderLess
          className="absolute top-2 right-2 hover:bg-transparent"
          onClick={() => {
            handleClose();
          }}
          Icon={XClose}
        />

        <Show if={view === View.ChooseUnit}>
          <p className="mb-0 mt-2 text-sm text-content-400 max-w-[calc(100%-40px)]">
            Application will be moved to the selected {unit}. Application will
            use the screening settings from the selected {unit}. Individual
            applications will keep the original set of requested information,
            but any missing reports will be added
          </p>
        </Show>
      </div>

      <Show if={view === View.ChooseUnit}>
        <div className="py-8 px-6 flex items-start gap-4 bg-background-900">
          <div className="w-full">
            <AsyncSelect
              id="property"
              label={Property}
              value={selectedPropertyId || ""}
              searchable={true}
              hideOptional
              // @ts-expect-error
              loadOptions={loadPropertyOptions}
              error={propertyError}
              options={[]}
              onChange={(e) => {
                if (!Array.isArray(e)) {
                  setSelectedPropertyId(Number(e.target.value));
                  setPropertyError("");
                  setSelectedUnitId(undefined);
                }
              }}
              additional={{
                page: 1
              }}
            />
          </div>
          <div className="w-full">
            <AsyncSelect
              id="unit"
              label={Unit}
              key={selectedPropertyId}
              searchable={true}
              hideOptional
              value={selectedUnitId || ""}
              options={[]}
              // @ts-expect-error
              loadOptions={loadUnitOptions}
              error={unitError}
              additional={{ page: 1, propertyId: selectedPropertyId }}
              onChange={(e) => {
                if (!Array.isArray(e)) {
                  setSelectedUnitId(Number(e.target.value));
                  setUnitError("");
                  setSelectedUnitName(e.label);
                }
              }}
            />
          </div>
        </div>
        <div className="flex justify-end px-6 py-4 border-t border-alpha-100 gap-2">
          <Button className="w-[103px]" onClick={onClose}>
            Back
          </Button>
          <Button
            className="w-[130px]"
            variant={ButtonVariant.PRIMARY}
            onClick={handleContinue}
            disabled={!selectedUnitId}
          >
            Continue
          </Button>
        </div>
      </Show>
      <Show if={view === View.SetRentOptions && selectedUnitId}>
        <SetRentAmountOptions
          rentChangeType={rentChangeType}
          setRentChangeType={setRentChangeType}
          currentApplicationRents={{
            monthly_rent_amount: application?.monthly_rent_amount,
            rent_amount: application?.rent_amount,
            home_rent_amount: application?.home_rent_amount,
            site_rent_amount: application?.site_rent_amount,
            housing_assistance_voucher_amount:
              application?.housing_assistance_voucher_amount,
            pet_rent_amount: application?.pet_rent_amount
          }}
          applicationId={applicationId}
          unitId={selectedUnitId}
          unitName={selectedUnitName || ""}
          manualRent={manualRentsInput}
          setManualRent={setManualRentsInput}
        />

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-alpha-100">
          <Button
            className="w-[125px]"
            onClick={() => setView(View.ChooseUnit)}
          >
            Back
          </Button>
          <Button
            className="w-[153px]"
            loading={isPending}
            variant={ButtonVariant.PRIMARY}
            disabled={
              rentChangeType === "manual" &&
              (!pms.isManageAmerica
                ? manualRentsInput?.rent_amount?.cents === undefined
                : manualRentsInput?.home_rent_amount?.cents === undefined ||
                  manualRentsInput.site_rent_amount?.cents === undefined)
            }
            onClick={() => {
              mutate({
                change_type: rentChangeType,
                ...manualRentsInput
              });
            }}
          >
            Confirm
          </Button>
        </div>
      </Show>
    </Modal>
  );
};
